/**
 * RelationshipStatusBadge — Feb 2026.
 *
 * Tiny popover-driven indicator that shows a contact's tenant-shared
 * relationship status (good / caution / warning) and lets the user flip it.
 * Status changes are routed through PUT /api/contacts/{id}/relationship-status
 * which logs a `note` activity into the Phase 3 timeline.
 *
 * Two render modes:
 *   compact (default) — small dot suitable for the dense contact list row.
 *   inline             — labeled chip suitable for the drawer header.
 *
 * data-testids:
 *   relationship-status-trigger-{contactId}
 *   relationship-status-option-{status}
 */

import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Shield, AlertTriangle, AlertOctagon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const STATUS_META = {
  good: {
    label: 'Good',
    icon: Shield,
    dotClass: 'bg-emerald-500',
    chipClass: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    title: 'Good standing',
    description: 'No flag. Default.',
  },
  caution: {
    label: 'Caution',
    icon: AlertTriangle,
    dotClass: 'bg-amber-500',
    chipClass: 'bg-amber-50 text-amber-700 border-amber-200',
    title: 'Caution',
    description: 'Visible flag; does NOT block bulk actions.',
  },
  warning: {
    label: 'Warning',
    icon: AlertOctagon,
    dotClass: 'bg-red-500',
    chipClass: 'bg-red-50 text-red-700 border-red-200',
    title: 'Warning',
    description: 'Bulk actions require explicit confirm before including.',
  },
};

export default function RelationshipStatusBadge({
  contact,
  variant = 'compact',
  onChange,
  className = '',
}) {
  const status = contact?.relationship_status || 'good';
  const reason = contact?.relationship_status_reason || '';
  const meta = STATUS_META[status] || STATUS_META.good;
  const Icon = meta.icon;

  const [open, setOpen] = useState(false);
  const [draftReason, setDraftReason] = useState(reason);
  const [submitting, setSubmitting] = useState(false);

  const handleSet = async (next) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await axios.put(`${API}/contacts/${contact.id}/relationship-status`, {
        status: next,
        reason: draftReason || null,
      });
      toast.success(`Status set to ${STATUS_META[next].label.toLowerCase()}`);
      setOpen(false);
      onChange?.(res.data.contact);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setDraftReason(reason); }}>
      <PopoverTrigger asChild>
        {variant === 'inline' ? (
          <button
            type="button"
            onClick={(e) => e.stopPropagation()}
            className={`inline-flex items-center gap-1.5 text-xs px-2 py-0.5 border rounded-full hover:opacity-90 transition-opacity ${meta.chipClass} ${className}`}
            title={`${meta.title} — click to change`}
            data-testid={`relationship-status-trigger-${contact.id}`}
          >
            <Icon className="w-3 h-3" />
            <span>{meta.label}</span>
          </button>
        ) : (
          <button
            type="button"
            onClick={(e) => e.stopPropagation()}
            className={`relative inline-flex items-center justify-center w-4 h-4 rounded-full hover:scale-110 transition-transform ${className}`}
            title={`${meta.title}${reason ? ` — ${reason}` : ''} · click to change`}
            data-testid={`relationship-status-trigger-${contact.id}`}
          >
            <span className={`w-2.5 h-2.5 rounded-full ${meta.dotClass} ring-2 ring-background`} />
            {status === 'warning' && (
              <span className={`absolute inset-0 w-4 h-4 rounded-full ${meta.dotClass} opacity-30 animate-ping`} />
            )}
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent
        className="w-64 p-3"
        align="start"
        onClick={(e) => e.stopPropagation()}
        data-testid={`relationship-status-popover-${contact.id}`}
      >
        <div className="space-y-2">
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Relationship status
          </div>
          {Object.entries(STATUS_META).map(([key, m]) => {
            const SIcon = m.icon;
            const isActive = key === status;
            return (
              <button
                key={key}
                type="button"
                onClick={() => handleSet(key)}
                disabled={submitting}
                data-testid={`relationship-status-option-${key}`}
                className={`w-full flex items-start gap-2 p-2 rounded-md border text-left transition-colors ${
                  isActive ? `${m.chipClass} border-current` : 'border-transparent hover:bg-muted'
                }`}
              >
                <span className={`mt-0.5 w-2 h-2 rounded-full ${m.dotClass} shrink-0`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 text-sm font-medium">
                    <SIcon className="w-3 h-3" />
                    {m.label}
                    {isActive && <span className="text-[10px] text-muted-foreground">(current)</span>}
                  </div>
                  <div className="text-[11px] text-muted-foreground leading-snug">{m.description}</div>
                </div>
              </button>
            );
          })}
          <div className="pt-1">
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Reason (optional, shared with firm)
            </label>
            <Textarea
              value={draftReason}
              onChange={(e) => setDraftReason(e.target.value)}
              rows={2}
              placeholder="e.g. Late on docs, disputed invoice…"
              className="mt-1 text-xs"
              data-testid={`relationship-status-reason-${contact.id}`}
            />
            <p className="text-[10px] text-muted-foreground mt-1">
              Logged to the activity timeline on save.
            </p>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
